import { PlusIcon, LightbulbIcon } from '@buffer-mono/popcorn'

import type {
  CreateNewPostInComposerData,
  OpenComposerCommonOptions,
} from '~publish/hooks/usePostComposer'

import { PRIORITIES } from '../constants'
import { createAction } from './createAction'
import type { CommandBarAction } from './types'

/**
 * Options for building content creation actions.
 */
interface BuildCreationActionsOptions {
  /** Function to open the post composer with initial data */
  createNewPostInComposer: (
    data: CreateNewPostInComposerData,
    options?: OpenComposerCommonOptions,
  ) => Promise<void>
  /** Function to open the idea composer */
  openIdeaComposer?: () => void
  /** Whether the user is on a legacy plan */
  isLegacyPlan: boolean
  /** Single channel name for dynamic label (e.g., "Create post for Instagram") */
  singleChannelName?: string
  /** Single channel ID for pre-selecting the channel in composer */
  singleChannelId?: string
}

export function buildCreationActions({
  createNewPostInComposer,
  openIdeaComposer,
  isLegacyPlan,
  singleChannelName,
  singleChannelId,
}: BuildCreationActionsOptions): CommandBarAction[] {
  const actions: CommandBarAction[] = []

  // Dynamic label for single channel mode (guard against empty strings)
  const channelName = singleChannelName?.trim() || undefined
  const postLabel = channelName
    ? `Create post for ${channelName}`
    : 'Create New Post'
  const postDescription = channelName
    ? `Start creating a new post for ${channelName}`
    : 'Start creating a new post'

  // Channel selection for composer - pre-select single channel if available
  const channelSelection = singleChannelId ? [singleChannelId] : 'all'

  // Create New Post
  actions.push(
    createAction({
      id: 'create-new-post',
      name: postLabel,
      keywords: [
        'create',
        'new',
        'post',
        'compose',
        'write',
        'schedule',
        'publish',
        ...(channelName ? [channelName.toLowerCase()] : []),
      ],
      icon: <PlusIcon />,
      section: 'quick-actions',
      priority: PRIORITIES.createPost,
      description: postDescription,
      perform: () =>
        createNewPostInComposer({
          channels: channelSelection,
          cta: 'publish-commandBar-newPost-createNewPost-1',
        }),
    }),
  )

  // Create Idea - not available on legacy plans
  if (!isLegacyPlan && openIdeaComposer) {
    actions.push(
      createAction({
        id: 'create-idea',
        name: 'Create Idea',
        keywords: ['create', 'idea', 'save', 'note', 'brainstorm', 'content'],
        icon: <LightbulbIcon />,
        section: 'quick-actions',
        priority: PRIORITIES.calendar,
        description: 'Save a content idea for later',
        perform: openIdeaComposer,
      }),
    )
  }

  return actions
}
